<script lang="ts">
  import Table, {
    type ColumnDef,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import WorkOrderTableCell from "../../../work-order-table-cell"
  import type { WorkOrderRow } from "../../../../_logic/work-order-table-model"
  import CancelReplanAppointmentCell from "../../../cancel-replan-appointment-cell"
  import { createStaticRowsRefreshKey } from "../../../../_logic/rows"
  import type {
    ConfirmAppointmentRow,
    ConfirmAppointmentsWorkflowEvent,
    ConfirmEventsStepOutput,
    SendConfirmationMailStepOutput,
  } from "../../_shared/_logic/confirm-appointments-contract"
  import { confirmableMailEventIds } from "../../_shared/_logic/rows"
  import {
    createConfirmationMailFilters,
    filterConfirmationMailRows,
    mailStatusForRow,
    type ConfirmationMailFilterState,
  } from "../../_shared/_logic/filters"
  import BatchFilterBar from "../../../../../../../_shared/_modules/batch-filter-bar"
  import {
    batchDateRangeInputValue,
    hasUsefulBatchDateRange,
    hasUsefulBatchSearch,
    hasUsefulBatchSelectOptions,
    normalizeBatchDateRange,
    uniqueBatchOptions,
  } from "../../../../../../../_shared/_logic/filter-values"
  import {
    STABLE_ROW_NUMBER_COLUMN_ID,
    createStableRowNumberState,
    getStableSortedRowPage,
    stableRowNumberColumn,
    stableRowNumberSort,
    withStableRowNumbers,
  } from "../../../../../../../_shared/_logic/stable-row-numbers"
  import ConfirmationMailActionRow from "./_modules/confirmation-mail-action-row"
  import type {
    ConfirmationMailActionPhase,
    ConfirmationMailActionResult,
  } from "./actions/confirmationMail"
  import { buildConfirmationMailIntent } from "./build-confirmation-mail-intent"
  import { confirmationMailOperations } from "./operations"

  let {
    confirmOutput,
    output = null,
    readOnly = false,
    onBack,
    onEvent,
    onError,
  }: {
    confirmOutput: ConfirmEventsStepOutput
    output?: SendConfirmationMailStepOutput | null
    readOnly?: boolean
    onBack: () => void
    onEvent: (event: ConfirmAppointmentsWorkflowEvent) => Promise<void> | void
    onError: (message: string | null) => void
  } = $props()

  let actionPhase = $state<ConfirmationMailActionPhase>("idle")
  const submitting = $derived(actionPhase !== "idle")
  let tableFilters = $state<ConfirmationMailFilterState>(createConfirmationMailFilters())
  const rowNumberState = createStableRowNumberState()

  const eligibleEventIds = $derived(confirmableMailEventIds(confirmOutput))
  const confirmationMailIntents = $derived({
    skip: buildConfirmationMailIntent({ action: "skip", eligibleEventIds }),
    send: buildConfirmationMailIntent({ action: "send", eligibleEventIds }),
  })
  const rows = $derived(confirmOutput.rows.filter((row) => eligibleEventIds.includes(row.event.id)))
  const numberedRows = $derived(withStableRowNumbers(rows, rowNumberState))
  const visibleRows = $derived(filterConfirmationMailRows(numberedRows, tableFilters, output))
  const refreshKey = $derived(createStaticRowsRefreshKey(visibleRows))
  const mailStatusOptions = $derived(
    uniqueBatchOptions(
      rows.map((row) => mailStatusForRow(row, output)),
      "Alle Mailstatus",
    ),
  )
  const appointmentDateValues = $derived(rows.map((row) => row.event.start_at))
  const columns: ColumnDef[] = [
    stableRowNumberColumn(),
    { id: "order", label: "Auftrag" },
    { id: "appointment", label: "Bestätigter Termin" },
    { id: "status", label: "Mail" },
  ]
  const getRows: TableGetRows = (params) => {
    const { limit, offset } = params
    const sort = (params as { sort?: typeof stableRowNumberSort }).sort

    return getStableSortedRowPage({
      rows: visibleRows,
      columns,
      sort,
      limit,
      offset,
    })
  }
  const orderColumn: ColumnDef = { id: "orderSummary", label: "Auftrag" }

  const resultForRow = (row: ConfirmAppointmentRow) =>
    output?.items.find((item) => item.event_id === row.event.id) ?? null
  const updateFilter = (fieldId: string, value: unknown) => {
    tableFilters = {
      ...tableFilters,
      [fieldId]: fieldId === "dateRange" ? normalizeBatchDateRange(value) : String(value ?? ""),
    }
  }

  const handleMailResult = async (result: ConfirmationMailActionResult) => {
    await onEvent({ type: "mail.completed", output: result.output })
  }
</script>

<section class="mail-step">
  <ConfirmationMailActionRow
    intents={confirmationMailIntents}
    operations={confirmationMailOperations}
    {onBack}
    onSubmissionResult={handleMailResult}
    onPhaseChange={(phase) => (actionPhase = phase)}
  >
    {#snippet children(primaryAction, footerActions)}
      <TableStepLayout
        titleActions={readOnly ? null : primaryAction}
        title="Bestätigungsmails"
        count={visibleRows.length}
        description="Versenden Sie optional Bestätigungsmails für die erfolgreich bestätigten Termine."
      >
        {#snippet filters()}
          <BatchFilterBar
            fields={[
              {
                id: "search",
                type: "text",
                placeholder: "Suche",
                value: tableFilters.search,
                enabled: hasUsefulBatchSearch(rows.length),
              },
              {
                id: "mailStatus",
                type: "select",
                placeholder: "Mailstatus",
                value: tableFilters.mailStatus,
                options: mailStatusOptions,
                enabled: hasUsefulBatchSelectOptions(mailStatusOptions, rows.length),
              },
              {
                id: "dateRange",
                type: "dateRange",
                placeholder: "Terminzeitraum",
                value: batchDateRangeInputValue(tableFilters.dateRange),
                enabled: hasUsefulBatchDateRange(appointmentDateValues, rows.length),
              },
            ]}
            disabled={submitting || readOnly}
            onChange={updateFilter}
          />
        {/snippet}

        <Table
          {columns}
          {getRows}
          {refreshKey}
          defaultSort={stableRowNumberSort}
          theme="light"
          mode="table-scroll"
          pageSize={80}
          showActions={false}
        >
          {#snippet tableCell({ row, column })}
            {@const item = row as ConfirmAppointmentRow}
            {#if column.id === STABLE_ROW_NUMBER_COLUMN_ID}
              {item[STABLE_ROW_NUMBER_COLUMN_ID]}
            {:else if column.id === "order"}
              {#if item.workOrderRow}
                <WorkOrderTableCell row={item.workOrderRow as WorkOrderRow} column={orderColumn} />
              {:else}
                <span
                  >{item.workOrder?.order_number
                    ? `Auftrag ${item.workOrder.order_number}`
                    : "Auftrag"}</span
                >
              {/if}
            {:else if column.id === "appointment"}
              <CancelReplanAppointmentCell event={item.event} />
            {:else if column.id === "status"}
              {@const result = resultForRow(item)}
              <div class="mail-status">
                <strong>{result?.status ?? "bereit"}</strong>
                {#if result?.detail || result?.skipped_reason}
                  <span>{result.detail ?? result.skipped_reason}</span>
                {/if}
              </div>
            {/if}
          {/snippet}
        </Table>

        {#snippet footer()}
          {#if !readOnly}
            {@render footerActions()}
          {/if}
        {/snippet}
      </TableStepLayout>
    {/snippet}
  </ConfirmationMailActionRow>
</section>

<style>
  .mail-step {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .mail-status {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    color: var(--text-invers-100);
  }

  .mail-status span {
    font-size: 0.78rem;
    color: color-mix(in srgb, var(--text-invers-100) 70%, transparent);
  }
</style>
